/**
 * Godgift brand mark — overlay-tier asset (two-stage seam, design-language §9).
 *
 * P4.3 PLACEHOLDER. The client's real vector logo is still an open input
 * (client-attention.md §1 / Q9). Until it lands, this renders a serif-"G" monogram lockup in
 * brushed brass — an intentional, gallery-grade mark that is NOT a generic sparkle badge (the
 * dev-95/96 "AI-generic tell" the R28 start-screen pass replaces).
 *
 * SWAP CONTRACT (P4.3): when the client's vector logo arrives, replace ONLY this file's SVG body
 * with the real artwork (keep the `size` prop + the `aria`/`title` contract). No consumer changes:
 * every surface imports `<BrandMark />` from here, so the swap is a single-file drop — exactly the
 * two-stage discipline the branding/<overlay>/ layer exists for.
 *
 * Tokens only (brass via the --foil-brass gradient + --c-line hairline), both themes, no motion,
 * no emoji. Decorative by default (aria-hidden); pass `title` for an accessible label.
 */

type Props = {
  /** Rendered square size in px (the monogram sits in a rounded brass-hairline plinth). */
  size?: number;
  /** Optional accessible label; when omitted the mark is decorative (aria-hidden). */
  title?: string;
  className?: string;
};

export function BrandMark({ size = 56, title, className }: Props) {
  // A unique gradient id per instance so multiple marks on one screen don't collide.
  const gid = `godgift-foil-${title ? title.replace(/\W+/g, '') : 'mark'}`;
  return (
    <span
      className={`godgift-brandmark grid place-items-center rounded-2xl ${className ?? ''}`}
      style={{ width: size, height: size }}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      <svg
        width={size}
        height={size}
        viewBox="0 0 56 56"
        fill="none"
        focusable="false"
        aria-hidden="true"
      >
        <title>{title ?? 'Godgift International'}</title>
        <defs>
          {/* Brushed-brass foil — mirrors the --foil-brass token stops so the mark reads as the
              same metal as the rest of the accent language (design-language §2). */}
          <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#e7cd87" />
            <stop offset="0.4" stopColor="#cda74f" />
            <stop offset="0.74" stopColor="#9a7a35" />
            <stop offset="1" stopColor="#d8bd76" />
          </linearGradient>
        </defs>
        {/* Plinth — a soft rounded well with a hairline, so the monogram sits in the gallery. */}
        <rect
          x="1"
          y="1"
          width="54"
          height="54"
          rx="15"
          fill="var(--c-accent-soft)"
          stroke="var(--c-line-strong)"
          strokeWidth="1"
        />
        {/* Serif "G" in foil. Drawn as a stroked path (not a font glyph) so it renders identically
            regardless of the loaded display face and needs no font to be present. */}
        <path
          d="M37.5 20.3a11 11 0 1 0 1.2 14.9V28.4H28.9"
          stroke={`url(#${gid})`}
          strokeWidth="3.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    </span>
  );
}
